import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { generateTable } from "@/config/dashboard/general";
import {
  sculptureEditionsColumns,
  sculptureEditionsColumnsVisibility,
} from "@/config/dashboard/sculpture-editions";
import { Attachment, SculptureEdition } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { GridCardWithActions } from "./GridCardWithActions";
import { SculptureEditionLinkGridCard } from "./relatedLinkGridCards";

const STORAGE_KEY = "sculpture-edition-links-table";

/**
 * ShowElements n’appelle getAttachmentsByDocId que si `value` est falsy.
 * Un tableau vide est truthy en JS, donc sans ça aucune image ne s’affiche
 * quand l’API liste renvoie `attachments: []` alors que la fiche a des médias.
 */
function attachmentsPreviewValue(
  attachments: SculptureEdition["attachments"],
): Attachment[] {
  return attachments?.length
    ? attachments
    : (undefined as unknown as Attachment[]);
}

interface SculptureEditionLinksProps {
  id: string;
  type: LinkableType;
}

const SculptureEditionLinks = ({ id, type }: SculptureEditionLinksProps) => {
  const { t } = useTranslation();

  const [paginationRelated, setPaginationRelated] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedEditions } = useGetRelatedFields(
    id,
    type,
    "relatedSculptureEditions",
    paginationRelated.pageSize,
    paginationRelated.page * paginationRelated.pageSize,
  );

  const { data: countRelated } = useCountRelatedFields(id, type, [
    "relatedSculptureEditions",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedSculptureEditions",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedSculptureEditions",
  );

  const [searchContent, setSearchContent] = useState("");
  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [paginationNotRelated, setPaginationNotRelated] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: notRelated } = useGetNotRelatedResource<SculptureEdition>({
    resultType: "sculptureEdition",
    resourceId: id,
    pagination: paginationNotRelated,
    searchQuery: searchContent,
    anchorResourceType: type,
  });

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const handleCreate = useCallback(
    (editionId: string) => {
      createRelatedFields([id, editionId]);
    },
    [createRelatedFields, id],
  );

  const handleDelete = useCallback(
    (editionId: string) => {
      deleteRelation(editionId);
    },
    [deleteRelation],
  );

  const baseColumns = useMemo(
    () => generateTable(sculptureEditionsColumns, "sculptureEdition", t),
    [t],
  );

  const renderSculptureEditionRowActions = useCallback(
    (item: SculptureEdition) => (
      <RelatedRecordRowActions
          isAddMode={isAddMode}
          resourceId={item.id}
          previewType="sculptureEdition"
          previewElements={[
            {
              label: t("sculpture.form.sculptureEditionNumeroEnregistrement"),
              value: item.sculptureEditionNumeroEnregistrement,
              type: "text",
            },
            {
              label: t("sculptureEditionNotes"),
              value: item.sculptureEditionNotes,
              type: "richtext",
            },
            {
              label: t("attachments"),
              value: attachmentsPreviewValue(item.attachments),
              type: "image",
            },
          ]}
          onDelete={() => handleDelete(item.id)}
          onAdd={() => handleCreate(item.id)}
        />
    ),
    [handleCreate, handleDelete, isAddMode, t],
  );

  const columns: ColumnDef<SculptureEdition, any>[] = useMemo(() => {
    return [
      ...baseColumns,
      createActionsColumnDef<SculptureEdition>(({ row }) =>
        renderSculptureEditionRowActions(row.original),
      ),
    ];
  }, [baseColumns, renderSculptureEditionRowActions]);

  const defaultVisibility = {
    ...sculptureEditionsColumnsVisibility,
    actions: true,
  };
  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    },
  );

  const tableOptions = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnsOrder,
    state: { columnVisibility, columnOrder: columnsOrder },
  };

  const relatedPaginationProps = {
    pagination: paginationRelated,
    setPage: setPaginationRelated,
    totalSize: countRelated?.relatedSculptureEditions ?? 0,
  };

  return (
    <RelatedLinksListGridLayout
      storageKey={STORAGE_KEY}
      title={t("entrysculptureeditions")}
      isAddMode={isAddMode}
      onBack={handleAddMode}
      onToggleAdd={handleAddMode}
      searchInput={
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("search")}
            className="w-80"
            value={searchContent}
            onChange={(e) => setSearchContent(e.target.value)}
          />
        </div>
      }
      addModeTable={
        <DataTable
          columns={columns}
          data={notRelated?.data ?? []}
          pagination={{
            pagination: paginationNotRelated,
            setPage: setPaginationNotRelated,
            totalSize: notRelated?.totalSize ?? 0,
          }}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      listTable={
        <DataTable
          columns={columns}
          data={relatedEditions ?? []}
          pagination={relatedPaginationProps}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      gridContent={
        <>
          {relatedEditions?.map((row: SculptureEdition) => (
            <div key={row.id}>
              <GridCardWithActions actions={renderSculptureEditionRowActions(row)}>
                <SculptureEditionLinkGridCard
                item={row}
                t={t}
                onTextClick={() => setSelectedId(row.id)}
              />
              </GridCardWithActions>
              <ShowElements
                type="sculptureEdition"
                id={row.id}
                open={selectedId === row.id}
                onOpenChange={(open) => {
                  if (!open) setSelectedId(null);
                }}
                trigger={<span />}
                elements={[
                  {
                    label: t(
                      "sculpture.form.sculptureEditionNumeroEnregistrement",
                    ),
                    value: row.sculptureEditionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("sculptureEditionNotes"),
                    value: row.sculptureEditionNotes ?? "",
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: attachmentsPreviewValue(row.attachments),
                    type: "image",
                  },
                ]}
              />
            </div>
          ))}
        </>
      }
      relatedPagination={relatedPaginationProps}
    />
  );
};

export default SculptureEditionLinks;
